import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

// Types
export interface FeaturedProfessional {
  id: string;
  user_id?: string;
  business_name: string;
  slug: string;
  categories: string[];
  address_city: string;
  address_region?: string;
  profile_image: string;
  is_verified: boolean;
  first_name?: string;
  last_name?: string;
  average_rating?: number;
  review_count?: number;
  is_company?: boolean;
  created_at?: string;
  subscription_type?: 'base' | 'pro' | 'plus' | null;
  languages_spoken?: string[];
  tagline_it?: string;
  tagline_en?: string;
  tagline_es?: string;
}

export interface UpcomingEvent {
  id: string;
  title: string;
  slug: string;
  start_datetime: string;
  location_city: string;
  cover_image_url: string;
  current_participants: number;
  max_participants: number;
  event_type?: string;
  event_format?: string;
  price?: number;
  currency?: string;
}

export interface LatestJob {
  id: string;
  title: string;
  slug: string;
  contract_type: string;
  work_mode: string;
  location_city: string;
  salary_range_min?: number;
  salary_range_max?: number;
  salary_period?: string;
  inclusivity_tags: string[];
  published_at: string;
  companies: {
    id: string;
    name: string;
    logo_url?: string;
    is_verified: boolean;
  };
}

export interface HomeStats {
  professionals: number;
  users: number;
  reviews: number;
  events: number;
  companies: number;
}

// Fetch functions
async function fetchFeaturedProfessionals(): Promise<FeaturedProfessional[]> {
  // Load verified profiles
  const { data: profiles, error: profilesError } = await supabase
    .from("profiles")
    .select("id, user_id, business_name, slug, categories, address_city, address_region, profile_image, is_verified, first_name, last_name, average_rating, review_count, created_at, languages_spoken, phone, professional_email, whatsapp")
    .eq("is_verified", true)
    .order("created_at", { ascending: false })
    .limit(20); // Increased to compensate for filtered incomplete profiles

  if (profilesError) throw profilesError;

  // Filter incomplete profiles (same logic as search-professionals edge function)
  const completeProfiles = (profiles || []).filter(profile => {
    const hasCategories = profile.categories && profile.categories.length > 0;
    const hasContactInfo = !!(profile.phone || profile.professional_email || profile.whatsapp);
    const hasLocation = !!(profile.address_city || profile.address_region);
    const hasProfileImage = !!profile.profile_image;
    return hasCategories && hasContactInfo && hasLocation && hasProfileImage;
  });

  // Load active companies
  const { data: companies, error: companiesError } = await supabase
    .from("companies")
    .select("id, owner_user_id, name, slug, categories, address_city, address_region, logo_url, is_verified, created_at, tagline_it, tagline_en, tagline_es, languages_spoken")
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(6);

  if (companiesError) throw companiesError;

  // Get all user_ids to fetch subscriptions
  const profileUserIds = completeProfiles.map(p => p.user_id);
  const companyUserIds = (companies || []).map(c => c.owner_user_id);
  const allUserIds = [...new Set([...profileUserIds, ...companyUserIds])];

  // Fetch active subscriptions for these users (only non-expired)
  let subscriptionMap: Record<string, string> = {};
  if (allUserIds.length > 0) {
    const { data: subscriptions } = await supabase
      .from("subscriptions")
      .select("user_id, product_type")
      .in("user_id", allUserIds)
      .in("product_type", ["base", "pro", "plus"])
      .eq("status", "active")
      .gte("current_period_end", new Date().toISOString());
    
    if (subscriptions) {
      subscriptionMap = subscriptions.reduce((acc, sub) => {
        acc[sub.user_id] = sub.product_type;
        return acc;
      }, {} as Record<string, string>);
    }
  }

  // Fetch active spotlights
  const { data: spotlights } = await supabase
    .from("featured_professionals")
    .select("user_id")
    .eq("is_active", true)
    .eq("feature_type", "spotlight")
    .gte("end_date", new Date().toISOString());

  // Fetch active social_boost subscriptions
  const { data: socialBoosts } = await supabase
    .from("subscriptions")
    .select("user_id")
    .eq("product_type", "social_boost")
    .eq("status", "active")
    .gte("current_period_end", new Date().toISOString());

  // Combine both sets of boosted users
  const boostedUserIds = new Set([
    ...(spotlights?.map(s => s.user_id) || []),
    ...(socialBoosts?.map(s => s.user_id) || [])
  ]);

  // Map companies to FeaturedProfessional format
  const mappedCompanies: FeaturedProfessional[] = (companies || []).map(company => ({
    id: company.id,
    user_id: company.owner_user_id,
    business_name: company.name,
    slug: company.slug,
    categories: company.categories || [],
    address_city: company.address_city || '',
    address_region: company.address_region || '',
    profile_image: company.logo_url || '',
    is_verified: company.is_verified || false,
    is_company: true,
    created_at: company.created_at,
    tagline_it: company.tagline_it,
    tagline_en: company.tagline_en,
    tagline_es: company.tagline_es,
    languages_spoken: company.languages_spoken || [],
    subscription_type: (subscriptionMap[company.owner_user_id] as 'base' | 'pro' | 'plus') || null
  }));

  // Map profiles adding is_company = false
  const mappedProfiles: FeaturedProfessional[] = completeProfiles.map(profile => ({
    id: profile.id,
    user_id: profile.user_id,
    business_name: profile.business_name,
    slug: profile.slug,
    categories: profile.categories || [],
    address_city: profile.address_city || '',
    address_region: profile.address_region || '',
    profile_image: profile.profile_image || '',
    is_verified: profile.is_verified || false,
    first_name: profile.first_name,
    last_name: profile.last_name,
    average_rating: profile.average_rating,
    review_count: profile.review_count,
    is_company: false,
    created_at: profile.created_at,
    languages_spoken: profile.languages_spoken || [],
    subscription_type: (subscriptionMap[profile.user_id] as 'base' | 'pro' | 'plus') || null
  }));

  // Filter: show users with active subscription OR with active boost (spotlight/social_boost)
  const validSubscriptionTypes = ['base', 'pro', 'plus'];
  const allItems = [...mappedProfiles, ...mappedCompanies].filter(
    item => (item.subscription_type && validSubscriptionTypes.includes(item.subscription_type))
           || boostedUserIds.has(item.user_id || '')
  );

  // Sort by: 1) boost priority (spotlight/social_boost), 2) subscription priority (plus > pro > base), 3) created_at (newest first)
  const getPriorityScore = (item: FeaturedProfessional): number => {
    let score = 0;
    // Spotlight/Social Boost have maximum priority
    if (boostedUserIds.has(item.user_id || '')) score += 1000;
    // Then subscription tier
    if (item.subscription_type === 'plus') score += 300;
    if (item.subscription_type === 'pro') score += 200;
    if (item.subscription_type === 'base') score += 100;
    return score;
  };

  return allItems
    .sort((a, b) => {
      const scoreA = getPriorityScore(a);
      const scoreB = getPriorityScore(b);
      
      if (scoreB !== scoreA) {
        return scoreB - scoreA;
      }
      return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
    })
    .slice(0, 6);
}

async function fetchUpcomingEvents(): Promise<UpcomingEvent[]> {
  const { data, error } = await supabase
    .from("events")
    .select("id, title, slug, start_datetime, location_city, cover_image_url, current_participants, max_participants, event_type, event_format, price, currency")
    .eq("status", "published")
    .gte("start_datetime", new Date().toISOString())
    .order("start_datetime", { ascending: true })
    .limit(6);

  if (error) throw error;
  return data || [];
}

async function fetchHomeStats(): Promise<HomeStats> {
  const [profCount, uniqueUsersResult, reviewCount, eventCount, companyCount] = await Promise.all([
    supabase.from("profiles").select("id", { count: "exact", head: true }).eq("is_verified", true),
    supabase.rpc("count_unique_users"),
    supabase.from("reviews").select("id", { count: "exact", head: true }).eq("admin_approved", true),
    supabase.from("events").select("id", { count: "exact", head: true }).eq("status", "published"),
    supabase.from("companies").select("id", { count: "exact", head: true }).eq("is_active", true)
  ]);

  return {
    professionals: profCount.count || 0,
    users: uniqueUsersResult.data || 0,
    reviews: reviewCount.count || 0,
    events: eventCount.count || 0,
    companies: companyCount.count || 0
  };
}

async function fetchLatestJobs(): Promise<LatestJob[]> {
  const { data, error } = await supabase
    .from("job_postings")
    .select(`
      id, title, slug, contract_type, work_mode, location_city,
      salary_range_min, salary_range_max, salary_period, inclusivity_tags, published_at,
      companies:company_id (id, name, logo_url, is_verified)
    `)
    .eq("status", "active")
    .gte("expires_at", new Date().toISOString())
    .order("published_at", { ascending: false })
    .limit(6);

  if (error) throw error;
  return (data || []) as unknown as LatestJob[];
}

// Cache times
const STALE_TIME = 1000 * 60 * 5; // 5 minutes
const GC_TIME = 1000 * 60 * 15; // 15 minutes
const STATS_STALE_TIME = 1000 * 60 * 10; // 10 minutes for stats

// Placeholder data for instant loading
const PLACEHOLDER_PROFESSIONALS: FeaturedProfessional[] = [];
const PLACEHOLDER_EVENTS: UpcomingEvent[] = [];
const PLACEHOLDER_JOBS: LatestJob[] = [];
const PLACEHOLDER_STATS: HomeStats = {
  professionals: 0,
  users: 0,
  reviews: 0,
  events: 0,
  companies: 0
};

// Hooks
export function useFeaturedProfessionals() {
  return useQuery({
    queryKey: ['home', 'featured-professionals'],
    queryFn: fetchFeaturedProfessionals,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: PLACEHOLDER_PROFESSIONALS,
  });
}

export function useUpcomingEvents() {
  return useQuery({
    queryKey: ['home', 'upcoming-events'],
    queryFn: fetchUpcomingEvents,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: PLACEHOLDER_EVENTS,
  });
}

export function useHomeStats() {
  return useQuery({
    queryKey: ['home', 'stats'],
    queryFn: fetchHomeStats,
    staleTime: STATS_STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: PLACEHOLDER_STATS,
  });
}

export function useLatestJobs() {
  return useQuery({
    queryKey: ['home', 'latest-jobs'],
    queryFn: fetchLatestJobs,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    placeholderData: PLACEHOLDER_JOBS,
  });
}

// Hook for realtime subscriptions that invalidate cache
export function useHomeRealtimeSubscriptions() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('home-stats-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'stats'] });
        queryClient.invalidateQueries({ queryKey: ['home', 'featured-professionals'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_roles' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'stats'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reviews' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'stats'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'stats'] });
        queryClient.invalidateQueries({ queryKey: ['home', 'upcoming-events'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'stats'] });
        queryClient.invalidateQueries({ queryKey: ['home', 'featured-professionals'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_postings' }, () => {
        queryClient.invalidateQueries({ queryKey: ['home', 'latest-jobs'] });
      })
      .subscribe((status, err) => {
        // Silently handle subscription errors to prevent console noise during Lighthouse scans
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed silently - realtime updates will not work but app functions normally
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
